import { useCallback, useRef, useState } from 'react';

/**
 * One-shot geolocation with explicit, friendly states.
 *
 * Deliberately manual: nothing asks for the permission until the surveyor taps
 * "Use Current Location", and a denied permission is remembered for the session
 * so the app never spams the prompt.
 */

export type GeolocationStatus =
  'idle' | 'locating' | 'success' | 'denied' | 'insecure' | 'unavailable' | 'timeout' | 'error';

export interface GeolocationFix {
  latitude: number;
  longitude: number;
  accuracy: number;
  capturedAt: number;
}

export interface UseGeolocationResult {
  status: GeolocationStatus;
  fix: GeolocationFix | null;
  message: string | null;
  /** True once the browser has told us the user said no. */
  permanentlyDenied: boolean;
  requestLocation: () => Promise<GeolocationFix | null>;
  reset: () => void;
}

/** Accuracy worse than this is reported to the user as a warning. */
export const LOW_ACCURACY_THRESHOLD_M = 50;

const OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 20_000,
  maximumAge: 0,
};

export function useGeolocation(): UseGeolocationResult {
  const [status, setStatus] = useState<GeolocationStatus>('idle');
  const [fix, setFix] = useState<GeolocationFix | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const deniedRef = useRef(false);

  const reset = useCallback(() => {
    setStatus('idle');
    setFix(null);
    setMessage(null);
  }, []);

  const requestLocation = useCallback(async (): Promise<GeolocationFix | null> => {
    /*
     * Browsers only expose geolocation in a secure context. Over plain HTTP —
     * which is what LAN testing from a phone looks like (http://192.168.x.x) —
     * iOS Safari reports a PERMISSION_DENIED that has nothing to do with the
     * user's choice. Checking first lets us say what is actually wrong instead
     * of telling the surveyor they refused something they were never asked.
     * `localhost` is exempt, so desktop development is unaffected.
     */
    if (typeof window !== 'undefined' && window.isSecureContext === false) {
      setStatus('insecure');
      setMessage(
        'Live location needs a secure (https) connection. This page is served over plain http, ' +
          'so the phone will not share GPS. Enter the location manually, or open the deployed https version of the app.',
      );
      return null;
    }

    if (!('geolocation' in navigator)) {
      setStatus('unavailable');
      setMessage('This device or browser does not provide GPS. Enter the location manually.');
      return null;
    }
    if (deniedRef.current) {
      setStatus('denied');
      setMessage(
        'Location permission was refused. Allow it in your browser settings, or enter the location manually.',
      );
      return null;
    }

    setStatus('locating');
    setMessage('Finding your location…');

    return new Promise<GeolocationFix | null>((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const result: GeolocationFix = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            capturedAt: position.timestamp || Date.now(),
          };
          setFix(result);
          setStatus('success');
          setMessage(
            result.accuracy > LOW_ACCURACY_THRESHOLD_M
              ? `Location found, but accuracy is low (±${Math.round(result.accuracy)} m). Move to open sky or correct it manually.`
              : `Location found (±${Math.round(result.accuracy)} m).`,
          );
          resolve(result);
        },
        (error) => {
          switch (error.code) {
            case error.PERMISSION_DENIED:
              deniedRef.current = true;
              setStatus('denied');
              setMessage(
                'Location permission was refused. You can still enter the location and district manually.',
              );
              break;
            case error.POSITION_UNAVAILABLE:
              setStatus('unavailable');
              setMessage(
                'GPS is not available right now. Move to open sky and try again, or enter it manually.',
              );
              break;
            case error.TIMEOUT:
              setStatus('timeout');
              setMessage('Finding the location took too long. Try again, or enter it manually.');
              break;
            default:
              setStatus('error');
              setMessage('Could not read the location. Enter it manually.');
          }
          resolve(null);
        },
        OPTIONS,
      );
    });
  }, []);

  return {
    status,
    fix,
    message,
    permanentlyDenied: deniedRef.current,
    requestLocation,
    reset,
  };
}
